// Copyright (c) Docugami, Inc. All rights reserved.

import { normalizeFragmentText } from '../doc_hosting/taskpane/TaskpaneCardHelpers';
import { Fragment } from '../../networking/api';
import { AaCard } from './AaCard';
import { ClauseCardType } from '../../hooks/aaCardClauses';
import { dgIconNames } from '../../hooks/dgIcons';
import { IDocumentDgml } from 'hooks/documentDgml';
import { Logger, LoggerCategory } from 'utils/Logger';
import { Telemetry } from 'telemetry/TelemetryManager';
import { AAInsertClauseIntoDocumentSchema, AARemoveClauseFromDocumentSchema, AAReplaceClauseInDocumentSchema, TelemetryEvent } from 'telemetry/TelemetryEvents';
import { aaMissingClauseColor, aaReviewClauseColor, aaVariantClauseColor, homeGray } from 'components/shell/presentation/AppStyles';
import { stripListItem } from 'components/doc_hosting/doc_hosts/word/v3/DgGenerateFormattedContent';
import { useAppSelector } from 'hooks/redux';
import { DgWordCanvasProxy } from 'components/doc_hosting/doc_hosts/word/v3/DgWordCanvasProxy';
import { IChunkClauseSuggestion, IClauseSuggestion, IInsertSuggestion } from './ClauseSuggestionsList';
import { ICommandButton } from 'components/cards/CommandButton';
import { emptyCommand, getHideClauseButton, useHideClauseCommand } from 'hooks/ClauseCommands';
import { useGetLabelsQuery } from 'store/frontend/frontendSlice';
import { createFolderId } from 'components/file_management/file_helpers';

const invalidAssistedAuthoringLabels = ["unknown", "unknown-label"];
function getValidLabelOrEmpty(label?: string): string {
    return !!label && !invalidAssistedAuthoringLabels.includes(label) ? label : '';
}

interface CardProps {
    item: IClauseSuggestion;
    documentId: string;
    selectedCardId: string;
    selectClause: (id: string, fragment?: Fragment) => void;
    dgWordCanvasProxy: DgWordCanvasProxy | null;
    hidden?: boolean;
}

interface ChunkCardProps extends CardProps {
    item: IChunkClauseSuggestion;
}

function useExistingClauseLabel(docsetId: string, labelId?: string, fallbackLabel?: string, containerName?: string) {
    const { data } = useGetLabelsQuery(docsetId);

    let label = '';
    if (!!labelId) {
        const labelItem = data?.entities[labelId];
        if (!!labelItem) {
            label = labelItem.label!;
        }
    }

    if (!label) {
        label = `${getValidLabelOrEmpty(fallbackLabel) || "Part"} of ${getValidLabelOrEmpty(containerName) || 'Document'}`;
    }

    return label;
}

interface ReplaceCardProps extends ChunkCardProps {
    currentFragment: Fragment;
}

export const ReplaceCard = (props: ReplaceCardProps): JSX.Element | null => {
    const { item, dgWordCanvasProxy } = props;
    const instance = item.instance;
    const assistedAuthoringModel = useAppSelector((state) => state.settings.assistedAuthoring.currentModel);

    const { clusterId } = createFolderId(props.documentId);

    const hideCommand = useHideClauseCommand(props.documentId, item.id, item.type, !!props.hidden);
    const label = useExistingClauseLabel(clusterId, item.label, item.containerHeader);

    if (!item.variants?.length) {
        return null;
    }

    const defaultVariant = item.variants[0];
    const text = stripListItem(normalizeFragmentText(defaultVariant.rawText ?? ''));

    function replace() {
        const dgmlToInsert = !!props.currentFragment ? props.currentFragment.dgml : defaultVariant.dgml;
        if (!!dgmlToInsert) {
            Telemetry.trackEvent<AAReplaceClauseInDocumentSchema>({
                model: assistedAuthoringModel,
                eventName: TelemetryEvent.AAReplaceClauseInDocument
            });
            if (dgWordCanvasProxy) {
                dgWordCanvasProxy?.replaceContent(
                    item.id ?? "",
                    dgmlToInsert);
            }
        }
    }

    const replaceCommand = ({
        icon: {
            name: dgIconNames.AssistedAuthoringReplace,
            height: 19,
            width: 15,
            defaultColor: homeGray
        },
        label: 'REPLACE',
        showLabel: true,
        backgroundColor: `${aaVariantClauseColor}80`,
        canExecute: true,
        onClick: replace
    })

    function getCommands(): ICommandButton[] {
        const hideButton = getHideClauseButton(hideCommand, !!props.hidden);
        if (!!props.hidden) {
            return [emptyCommand, hideButton];
        } else {
            return [replaceCommand, hideButton];
        }
    }

    return (
        <AaCard
            key={`vc_${instance.spanId!}`}
            id={instance.spanId!}
            cardType={ClauseCardType.VariantClause}
            header={label}
            text={text}
            probability={item.likelihood as number}
            fragmentProbability={item.fragmentLikelihood as number}
            activeView={instance.spanId === props.selectedCardId}
            actionCallback={replace}
            commands={getCommands()}
            selectedCallback={props.selectClause}
            fragments={item.variants}
            importance={props.item.importance}
            hidden={props.hidden} />
    )
}


interface RemoveCardProps extends ChunkCardProps {
    dgml: IDocumentDgml;
}

export const RemoveCard = (props: RemoveCardProps): JSX.Element | null => {
    const { item, dgWordCanvasProxy } = props;
    const instance = item.instance;
    const assistedAuthoringModel = useAppSelector((state) => state.settings.assistedAuthoring.currentModel);

    const hideCommand = useHideClauseCommand(props.documentId, item.id, item.type, !!props.hidden);

    const label = useExistingClauseLabel(props.dgml.docsetId, instance.labelId, item.label, item.containerHeader);
    const text = stripListItem(props.dgml.getTokensTextParser().getTextForTokenRange(instance.tokenRange!, true).join("\n"));

    function remove() {
        if (!!item.id) {
            Telemetry.trackEvent<AARemoveClauseFromDocumentSchema>({
                model: assistedAuthoringModel,
                eventName: TelemetryEvent.AARemoveClauseFromDocument
            });
            dgWordCanvasProxy?.deleteContent(instance.spanId!);
        }
    }

    const removeCommand = ({
        icon: {
            name: dgIconNames.AssistedAuthoringClear,
            height: 19,
            width: 15,
            defaultColor: homeGray
        },
        label: 'REMOVE',
        showLabel: true,
        backgroundColor: `${aaReviewClauseColor}66`,
        canExecute: true,
        onClick: remove
    })

    function getCommands(): ICommandButton[] {
        const hideButton = getHideClauseButton(hideCommand, !!props.hidden);
        if (!!props.hidden) {
            return [emptyCommand, hideButton];
        } else {
            return [removeCommand, hideButton];
        }
    }

    return (
        <AaCard
            key={`rc_${instance.spanId}`}
            id={instance.spanId!}
            cardType={ClauseCardType.ReviewClause}
            header={label}
            text={text}
            activeView={instance.spanId === props.selectedCardId}
            probability={item.likelihood as number}
            fragmentProbability={item.fragmentLikelihood as number}
            actionCallback={remove}
            commands={getCommands()}
            selectedCallback={props.selectClause}
            importance={props.item.importance}
            hidden={props.hidden} />
    )
}


interface InsertCardProps extends CardProps {
    item: IInsertSuggestion;
    currentFragment: Fragment;
}

export const InsertCard = (props: InsertCardProps): JSX.Element | null => {
    const { item, dgWordCanvasProxy } = props;
    const assistedAuthoringModel = useAppSelector((state) => state.settings.assistedAuthoring.currentModel);

    const hideCommand = useHideClauseCommand(props.documentId, item.id, item.type, !!props.hidden);

    const variants = item.variants;
    if (!variants?.length) {
        Logger.LogError(LoggerCategory.AssistedAuthoring, "Missing Chunk w/out Variations");
        return null;
    }

    const { precedingSiblingSpanId, followingSiblingSpanId, containerChunkSpanId } = props.item.positionInfo;
    const defaultVariant = variants[0];

    const text = stripListItem(normalizeFragmentText(defaultVariant.rawText ?? ''));
    const fallbackLabel = getValidLabelOrEmpty(defaultVariant.referencingLabel);
    const label = getValidLabelOrEmpty(item.label) || (!!fallbackLabel ? `Part of ${fallbackLabel}` : 'Paragraph');

    async function insert() {
        const dgmlToInsert = !!props.currentFragment ? props.currentFragment.dgml : defaultVariant.dgml;

        if (!!dgmlToInsert) {
            Telemetry.trackEvent<AAInsertClauseIntoDocumentSchema>({
                model: assistedAuthoringModel,
                eventName: TelemetryEvent.AAInsertClauseIntoDocument
            });

            await dgWordCanvasProxy?.insertContent(
                precedingSiblingSpanId,
                followingSiblingSpanId,
                containerChunkSpanId,
                item.id,
                dgmlToInsert
            );
        }
    }

    const insertCommand = ({
        icon: {
            name: dgIconNames.AssistedAuthoringPaste,
            height: 19,
            width: 15,
            defaultColor: homeGray
        },
        label: 'INSERT',
        showLabel: true,
        backgroundColor: aaMissingClauseColor,
        canExecute: true,
        onClick: insert
    })

    function getCommands(): ICommandButton[] {
        const hideButton = getHideClauseButton(hideCommand, !!props.hidden);
        if (!!props.hidden) {
            return [emptyCommand, hideButton];
        } else {
            return [insertCommand, hideButton];
        }
    }

    return (
        <AaCard
            key={`ic_${item.id}`}
            id={item.id}
            cardType={ClauseCardType.MissingClause}
            header={label}
            text={text}
            probability={item.likelihood as number}
            fragmentProbability={0}
            activeView={item.id === props.selectedCardId}
            actionCallback={insert}
            commands={getCommands()}
            selectedCallback={props.selectClause}
            fragments={variants}
            importance={item.importance}
            hidden={props.hidden} />
    )
}
